import type { LinkIcon } from '@flame/shared';
import { useEffect, useState } from 'preact/hooks';

/** A small inline SVG from an @mdi/js path string. */
export function Svg({ path, size = 20, label }: { path: string; size?: number; label?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden={label ? undefined : 'true'}
      aria-label={label}
      role={label ? 'img' : undefined}
      class="svg-icon"
    >
      <path d={path} fill="currentColor" />
    </svg>
  );
}

// The full MDI set is ~7k icons; load it only when a link actually uses one.
let mdiModule: Promise<Record<string, string>> | null = null;
const loadMdi = () =>
  (mdiModule ??= import('@mdi/js') as unknown as Promise<Record<string, string>>);

/** "github", "mdi-github", or "mdiGithub" → "mdiGithub". */
export function mdiExportName(name: string): string {
  const trimmed = name.trim();
  if (/^mdi[A-Z]/.test(trimmed)) return trimmed;
  return (
    'mdi' +
    trimmed
      .replace(/^mdi[-:]/, '')
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((w) => w[0]!.toUpperCase() + w.slice(1))
      .join('')
  );
}

function MdiIcon({ name, size, fallback }: { name: string; size: number; fallback: preact.ComponentChildren }) {
  const [path, setPath] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    loadMdi().then((mod) => live && setPath(mod[mdiExportName(name)] ?? null));
    return () => {
      live = false;
    };
  }, [name]);
  if (path === null) return <>{fallback}</>;
  return <Svg path={path ?? ''} size={size} />;
}

function Letter({ title, size }: { title: string; size: number }) {
  const letter = [...(title.trim() || '?')][0]!.toUpperCase();
  return (
    <span class="letter-icon" style={{ width: size, height: size, fontSize: size * 0.55 }} aria-hidden="true">
      {letter}
    </span>
  );
}

export function LinkIconView({ icon, title, size = 24 }: { icon: LinkIcon | null; title: string; size?: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [icon?.kind, icon?.value]);
  const letter = <Letter title={title} size={size} />;
  if (!icon || failed) return letter;
  if (icon.kind === 'mdi') return <MdiIcon name={icon.value} size={size} fallback={letter} />;
  const src =
    icon.kind === 'favicon' ? `/api/favicons/${encodeURIComponent(icon.value)}` : icon.value;
  return (
    <img
      class="link-img"
      src={src}
      width={size}
      height={size}
      alt=""
      loading="lazy"
      decoding="async"
      referrerpolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}
